import { Box, Container, Heading } from '@chakra-ui/react';
import PageHeader from 'components/page-header/PageHeader';
import PageTitleMobile from 'components/page-header/PageTitleMobile';
import { IPaginationResponse } from 'dtos/pagination.dto';
import { IStyleGuide } from 'interfaces/style-guide.interface';
import RootLayout from 'layouts/RootLayout';
import { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Head from 'next/head';
import Image from 'next/image';
import { NextPageWithLayout } from 'pages/_app';
import { useEffect, useState } from 'react';
import useSWR from 'swr';

type StyleGuideDetailPageProps = {
  data: IStyleGuide;
  locale: string | undefined;
};

const StyleGuideDetailPage: NextPageWithLayout<StyleGuideDetailPageProps> = ({
  data,
  locale,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { data: data2 } = useSWR<IStyleGuide>(
    data
      ? `${process.env.NEXT_PUBLIC_API_URL}/v1/public/style-guides/${data.permalink}?include=featuredImage`
      : null
  );

  const [optimisticData, setOptimisticData] = useState<IStyleGuide>(data);

  useEffect(() => {
    if (data2) {
      setOptimisticData(data2);
    }
  }, [data2]);

  return (
    <>
      <Head>
        <title>Lamptan Style Guide | แลมป์ตั้น</title>
      </Head>
      <Box>
        <PageHeader
          breadcrumb={[
            {
              label: 'Style Guide',
              href: '/style-guide',
            },
          ]}
        ></PageHeader>
        <PageTitleMobile>{`Style Guide`}</PageTitleMobile>
        <Container
          pt={{
            base: 8,
            md: 12,
          }}
          pb={{
            base: 8,
            md: 12,
          }}
        >
          <Box mb={6} display="flex" justifyContent="center">
            <Image
              src={`${process.env.NEXT_PUBLIC_FILE_URL}/${optimisticData?.featuredImage?.name}`}
              alt={optimisticData?.title}
              width={250}
              height={250}
            ></Image>
          </Box>
          <Box mb={6} pb={6} textAlign="center" position="relative">
            <Heading as="h1">
              {locale === 'th'
                ? optimisticData?.title
                : optimisticData?.title_en}
            </Heading>
            <Box
              height={1}
              width="100px"
              bg="primary.500"
              position="absolute"
              bottom={0}
              left={'50%'}
              transform="translateX(-50%)"
            ></Box>
          </Box>
          <Box
            className="entry-content"
            dangerouslySetInnerHTML={{
              __html: optimisticData?.content || '',
            }}
          ></Box>
        </Container>
      </Box>
    </>
  );
};

StyleGuideDetailPage.getLayout = (page) => {
  return (
    <RootLayout
      pb={{
        base: '50px',
        lg: 0,
      }}
    >
      {page}
    </RootLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  // log process.env.API_URL
  console.log('process.env.API_URL', process.env.API_URL);

  const response = await fetch(
    `${process.env.API_URL}/v1/public/style-guides/?limit=10&include=featuredImage`
  );

  const data: IPaginationResponse<IStyleGuide> = await response.json();

  console.log('data.docs', data);

  return {
    paths: data?.docs?.map((doc) => ({
      params: {
        permalink: doc.permalink,
      },
    })),
    fallback: true,
  };
};

export const getStaticProps: GetStaticProps<
  StyleGuideDetailPageProps
> = async ({ params, locale }) => {
  const _locale = locale || 'th';

  // get id from params
  const permalink = params?.permalink;

  // log params?.permalink
  console.log('params?.permalink', params?.permalink);

  // if permalink is not found
  if (!permalink) {
    return {
      notFound: true,
    };
  }

  let data: IStyleGuide | null = null;

  try {
    // get data
    const response = await fetch(
      `${process.env.API_URL}/v1/public/style-guides/${permalink}?include=featuredImage`
    );

    data = await response.json();
  } catch (error: any) {
    data = null;
  }

  if (!data) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      data: data,
      locale: _locale,
      ...(await serverSideTranslations(_locale, ['common'])),
      revalidate: 2,
    },
  };
};

export default StyleGuideDetailPage;
